/**
 * ============================================================================
 * DATE RANGE CONTROL — DCR Created Date From/To over the global filterQuery.
 * ============================================================================
 *
 * QuickSight dateTimePicker parity: two date inputs writing the
 * `dcrCreatedDate >= / <=` tokens (the model ANDs range bounds — spec
 * filter-atomization-metric-name / filter-control-shell R1.2). An empty input
 * clears its bound. From > To is applied as-is: an empty result is honest,
 * a silent swap would misrepresent what the user typed.
 *
 * Rendered inline to match the pill row (no FormField label stack): a muted
 * inline label + the two pickers. DatePicker has no inlineLabelText, so the
 * label is a plain span tied to the group via aria-label on each picker.
 */

import React from 'react';
import Box from '@amzn/awsui-components-core/box';
import DatePicker from '@amzn/awsui-components-core/date-picker';
import {
  dateBounds,
  withDateBound,
} from 'src/components/filters/controls/filterControlModel';
import { FilterQuery } from 'src/features/dashboard/dashboardSlice';

export interface DateRangeControlProps {
  query: FilterQuery;
  onQueryChange: (query: FilterQuery) => void;
}

export const DateRangeControl: React.FC<DateRangeControlProps> = ({
  query,
  onQueryChange,
}) => {
  const bounds = dateBounds(query);
  return (
    <div className="filter-shell__daterange">
      <Box color="text-body-secondary" fontSize="body-s">
        DCR Created:
      </Box>
      <DatePicker
        value={bounds.from}
        onChange={({ detail }) =>
          onQueryChange(withDateBound(query, 'from', detail.value))
        }
        placeholder="YYYY/MM/DD"
        ariaLabelledby=""
        ariaLabel="DCR created on or after"
        openCalendarAriaLabel={(selectedDate) =>
          `Choose start date${
            selectedDate ? `, selected date is ${selectedDate}` : ''
          }`
        }
      />
      <Box color="text-body-secondary" fontSize="body-s">
        –
      </Box>
      <DatePicker
        value={bounds.to}
        onChange={({ detail }) =>
          onQueryChange(withDateBound(query, 'to', detail.value))
        }
        placeholder="YYYY/MM/DD"
        ariaLabelledby=""
        ariaLabel="DCR created on or before"
        openCalendarAriaLabel={(selectedDate) =>
          `Choose end date${
            selectedDate ? `, selected date is ${selectedDate}` : ''
          }`
        }
      />
    </div>
  );
};
